import ApplyV2AttachmentsUploader from '@core/components/ApplyDialogV2/ApplyV2AttachmentsUploader'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import getAcceptFilesString from '@core/utils/helpers/getAcceptFilesString'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import ApplyV2InputText from '@core/components/ApplyDialogV2/ApplyV2InputText'
import ApplyV2Textarea from '@core/components/ApplyDialogV2/ApplyV2Textarea'
import ApplyV2InputTextarea from '@core/components/ApplyDialogV2/ApplyV2InputTextarea'
import ApplyV2Switch from '@core/components/ApplyDialogV2/ApplyV2Switch'
import ApplyV2DateSelectGroup from '@core/components/ApplyDialogV2/ApplyV2DateSelectGroup'
import ApplyV2Select from '@core/components/ApplyDialogV2/ApplyV2Select'
import FileUploaderV2 from '@core/components/ApplyDialogV2/FileUploaderV2'
import FileUploaderV2List from '@core/components/ApplyDialogV2/FileUploaderV2List'
import {
    usePostCourseFileV2ApplyMutation,
    usePostCourseV2ApplyMutation,
    usePutCourseV2ApplyMutation,
    useDeleteCourseFileV2ApplyMutation,
} from '@core/app/api/applyJobV2Api'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import type {
    ApplyV2FileAttachment,
    ApplyV2TempBase64File,
    ApplyV2TempCourse,
    ApplyV2TempCourseFile,
    V2CourseFile,
} from '@core/app/types/applyV2Type'
import { useFormik } from 'formik'
import React from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import './ApplyV2CoursesStepEdit.scss'

interface Props {
    className?: string
    onClose?: () => void
    item: ApplyV2TempCourse
}

const baseClass = 'ApplyV2CoursesStepEdit'

function ApplyV2CoursesStepEdit({ className, onClose = () => {}, item }: Props) {
    const { getTitle } = useApplyV2NavTranslations()
    const {
        postProfileData,
        postProfileError,
        isPostProfileLoading,
        isPostProfileSuccess,
        isSettingsLoading,
        isSettingsFetching,
        isFinalStep,
        setIsFinalStep,
        isPostApplied,
        onCloseApply,
        settingsData,
        navItems,
        storageData,
        postProfileRefetch,
    } = useApplyV2Context()
    const [postFileRequest, { isLoading: isFileLoading, error: fileError, data: fileData }] =
        usePostCourseFileV2ApplyMutation()
    const [deleteFileRequest, { isLoading: isDeleteFileLoading, error: deleteFileError, data: deleteFileData }] =
        useDeleteCourseFileV2ApplyMutation()
    const [postCourseRequest, { isLoading: isCourseLoading, error: courseErrors, data: courseData }] =
        usePutCourseV2ApplyMutation()
    const { t } = useTranslation()
    // const [loadingFileId, setLoadingFileId] = React.useState<string | null>(null)
    const [fileUploadingCount, setFileUploadingCount] = React.useState<number>(0)
    const [deletingFilesIds, setDeletingFilesIds] = React.useState<(string | number)[]>([])

    const validationSchema = Yup.object()
        .shape({
            courseName: Yup.string()
                .trim()
                .required(`${t('5653', 'Required')}`)
                .min(2, `${t('9763', 'Minimum')} 2`)
                .max(255, `${t('5339', 'Max')} 255`),
            courseDescription: Yup.string()
                .trim()
                .required(t('5653', 'Required') ?? '')
                .min(2, `${t('9763', 'Minimum')} 2`)
                .max(5000, `${t('5339', 'Max')} 5000`),
            issueDate: Yup.boolean(),
            expireDate: Yup.boolean(),
            noExpireDate: Yup.boolean(),
            startDateDay: Yup.string().when(['issueDate'], {
                is: true,
                then: Yup.string().required(t('5653', 'Required') ?? ''),
            }),
            startDateMonth: Yup.string().when(['issueDate'], {
                is: true,
                then: Yup.string().required(t('5653', 'Required') ?? ''),
            }),
            startDateYear: Yup.string().when(['issueDate'], {
                is: true,
                then: Yup.string().required(t('5653', 'Required') ?? ''),
            }),
            endDateDay: Yup.string().when(['expireDate'], {
                is: true,
                then: Yup.string().required(t('5653', 'Required') ?? ''),
            }),
            endDateMonth: Yup.string().when(['expireDate'], {
                is: true,
                then: Yup.string().required(t('5653', 'Required') ?? ''),
            }),
            endDateYear: Yup.string().when(['expireDate'], {
                is: true,
                then: Yup.string().required(t('5653', 'Required') ?? ''),
            }),
            files: Yup.array().max(5),
        })
        .test('startDate', 'Invalid start date', function (obj) {
            const { startDateDay, startDateMonth, startDateYear, issueDate } = obj || {}
            // console.log(startDateDay, startDateMonth, startDateYear, issueDate)
            // if (!issueDate) return true
            // if (!startDateDay || !startDateMonth || !startDateYear) return false
            // const date = new Date(
            //     parseInt(startDateYear as string, 10),
            //     parseInt(startDateMonth as string, 10) - 1,
            //     parseInt(startDateDay as string, 10)
            // )
            // console.log('Start date:', date)
            // return !isNaN(date.getTime())
            // console.log('Validation called with:', obj)
            return false
        })

    // console.log(item.files?.length)

    const startDateDay = item.startDate ? new Date(item.startDate).getDate().toString().padStart(2, '0') : ''
    const startDateMonth = item.startDate ? (new Date(item.startDate).getMonth() + 1).toString().padStart(2, '0') : ''
    const startDateYear = item.startDate ? new Date(item.startDate).getFullYear().toString() : ''
    const endDateDay = item.endDate ? new Date(item.endDate).getDate().toString().padStart(2, '0') : ''
    const endDateMonth = item.endDate ? (new Date(item.endDate).getMonth() + 1).toString().padStart(2, '0') : ''
    const endDateYear = item.endDate ? new Date(item.endDate).getFullYear().toString() : ''

    const formik = useFormik({
        initialValues: {
            courseName: item.courseName || '',
            courseDescription: item.courseDescription || '',
            issueDate: !!item.startDate,
            expireDate: !!item.endDate,
            noExpireDate: false,
            startDateDay: startDateDay,
            startDateMonth: startDateMonth,
            startDateYear: startDateYear,
            endDateDay: endDateDay,
            endDateMonth: endDateMonth,
            endDateYear: endDateYear,
            files: item.files || [],
        },
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            try {
                const uid = storageData?.postInitUid
                if (!uid) return
                setSubmitting(true)
                const startDate = values.issueDate
                    ? `${values.startDateYear}-${values.startDateMonth}-${values.startDateDay}`
                    : null
                const endDate = values.expireDate
                    ? `${values.endDateYear}-${values.endDateMonth}-${values.endDateDay}`
                    : null
                const res = await postCourseRequest({
                    uid,
                    body: {
                        uid: item.uid,
                        courseName: values.courseName,
                        courseDescription: values.courseDescription,
                        startDate,
                        endDate,
                        courseListId: 0,
                    },
                }).unwrap()
                await postProfileRefetch()
                onClose()
            } catch (error) {
                console.error('Submission error:', error)
            } finally {
                setSubmitting(false)
                console.log('Form submission completed')
            }
        },
        validationSchema,
        // validateOnMount: false,
        // validateOnBlur: true,
        // validateOnChange: true,
        enableReinitialize: true,
    })

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => {
                const result = reader.result as string
                const base64 = result.split('base64,')[1]
                resolve(base64)
            }
            reader.onerror = reject
            reader.readAsDataURL(file)
        })
    }

    const handleFileChangeHandler = async (files: { name: string; ext: string; base64: string }[]) => {
        if (!files.length) return
        const uid = storageData?.postInitUid
        if (!uid) return
        setFileUploadingCount(files.length)
        try {
            const promises = files.map(async (file) => {
                const response = await postFileRequest({
                    uid,
                    body: {
                        uid: item.uid,
                        file,
                    },
                }).unwrap()
                return response.data?.file
            })
            const uploadedFiles = await Promise.all(promises)
            await formik.setFieldValue('files', [...formik.values.files, ...uploadedFiles])
            await postProfileRefetch()
        } catch (error) {
            console.log('test')
            console.error('File upload error:', error)
        } finally {
            setFileUploadingCount(0)
        }
    }

    const handleFileChange = async (files: File[]) => {
        if (!files.length) return
        const uid = storageData?.postInitUid
        if (!uid) return
        try {
            setFileUploadingCount(files.length)
            const filesPromises = files.map(async (file) => {
                const base64 = await fileToBase64(file)
                const ext = file.name.split('.').pop() || file.type.split('/').pop() || ''
                const fileData = {
                    name: file.name,
                    ext,
                    base64,
                }
                const response = await postFileRequest({
                    uid,
                    body: {
                        file: fileData,
                        uid: item.uid,
                    },
                }).unwrap()
                return response.data?.file
            })
            const uploadedFiles = await Promise.all(filesPromises)
            await formik.setFieldValue('files', [...formik.values.files, ...uploadedFiles])
            await postProfileRefetch()
        } catch (error) {
            console.error('File upload error:', error)
        } finally {
            setFileUploadingCount(0)
        }
    }

    const handleFileDelete = async (
        file: ApplyV2TempCourseFile | File | ApplyV2FileAttachment | V2CourseFile | ApplyV2TempBase64File
    ) => {
        const uid = storageData?.postInitUid || ''
        if (!uid) return
        try {
            const fileUid = (file as ApplyV2TempCourseFile).uid
            setDeletingFilesIds((prev) => {
                return [...new Set([...prev, fileUid])]
            })
            // setLoadingFileId(fileUid)
            if (!fileUid) return
            const res = await deleteFileRequest({
                uid,
                courseUid: item.uid,
                fileUid,
            }).unwrap()
            console.log('File delete response:', res)
            // const updatedFiles = formik.values.files.filter((f) => f.uid !== fileUid)
            // await formik.setFieldValue('files', updatedFiles)
            await postProfileRefetch()
        } catch (error) {
            console.error('File delete error:', error)
            // Optionally, you can show an error message to the user
            if (deleteFileError) {
                console.error('Delete file error:', deleteFileError)
            }
        } finally {
            // setLoadingFileId(null)
            setDeletingFilesIds((prev) => {
                return prev.filter((file) => file !== uid)
            })
        }
    }

    return (
        <form className={clsx(baseClass, className)} onSubmit={formik.handleSubmit} noValidate>
            <div className={`${baseClass}__header`}>
                <div className={`${baseClass}__header__title`}>{getTitle('10389', 'Edit course / certificate')}</div>
                <ApplyV2Button
                    className={`${baseClass}__header__btn`}
                    size={'small'}
                    variant="transparent"
                    onClick={onClose}
                    leftIcon={
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M13.6239 2.19526C13.8843 2.45561 13.8843 2.87772 13.6239 3.13807L8.8524 7.90963L13.8047 12.8619C14.0651 13.1223 14.0651 13.5444 13.8047 13.8047C13.5444 14.0651 13.1223 14.0651 12.8619 13.8047L7.90959 8.85243L3.13807 13.6239C2.87772 13.8843 2.45561 13.8843 2.19526 13.6239C1.93491 13.3636 1.93491 12.9415 2.19526 12.6811L6.96678 7.90963L2.37605 3.31886C2.1157 3.05851 2.11571 2.6364 2.37606 2.37605C2.63641 2.11571 3.05852 2.11571 3.31887 2.37606L7.90959 6.96682L12.6811 2.19526C12.9415 1.93491 13.3636 1.93491 13.6239 2.19526Z"
                                fill="#19191A"
                            />
                        </svg>
                    }
                />
            </div>
            <ApplyV2InputText
                name="courseName"
                label={getTitle('63', 'Name')}
                value={formik.values.courseName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errorMessage={formik.touched.courseName && formik.errors.courseName}
                // errorMessage={formik.errors.courseName}
                isRequired
            />

            {/*<ApplyV2InputTextarea*/}
            {/*    name="courseDescription"*/}
            {/*    label={'DDD'}*/}
            {/*    value={formik.values.courseDescription}*/}
            {/*    onChange={formik.handleChange}*/}
            {/*    onBlur={formik.handleBlur}*/}
            {/*    errorMessage={formik.touched.courseDescription && formik.errors.courseDescription}*/}
            {/*    isRequired*/}
            {/*    rows={1}*/}
            {/*/>*/}
            <ApplyV2Switch
                name="issueDate"
                checked={formik.values.issueDate}
                // onChange={formik.handleChange}
                onChange={(e) => {
                    formik.setFieldValue('issueDate', e.target.checked)
                    if (!e.target.checked) {
                        formik.setFieldValue('startDateDay', '')
                        formik.setFieldValue('startDateMonth', '')
                        formik.setFieldValue('startDateYear', '')
                    }
                }}
                onBlur={formik.handleBlur}
                label={getTitle('8733', 'Issue date')}
            />

            {formik.values.issueDate && (
                <ApplyV2DateSelectGroup
                    // label={getTitle('7577', 'Expiry date')}
                    isRequired
                    names={{
                        day: 'startDateDay',
                        month: 'startDateMonth',
                        year: 'startDateYear',
                    }}
                    value={{
                        day: formik.values.startDateDay ?? startDateDay,
                        month: formik.values.startDateMonth ?? startDateMonth,
                        year: formik.values.startDateYear ?? startDateYear,
                    }}
                    onChange={({ day, month, year }) => {
                        formik.setFieldValue('startDateDay', day)
                        formik.setFieldValue('startDateMonth', month)
                        formik.setFieldValue('startDateYear', year)
                    }}
                    errors={{
                        day: formik.touched.startDateDay && formik.errors.startDateDay,
                        month: formik.touched.startDateMonth && formik.errors.startDateMonth,
                        year: formik.touched.startDateYear && formik.errors.startDateYear,
                    }}
                    onBlur={formik.handleBlur}
                    isDisabled={!formik.values.issueDate}
                    isFutureYears={false}
                    yearsRange={40}
                />
            )}
            <ApplyV2Switch
                name="expireDate"
                checked={formik.values.expireDate}
                // onChange={formik.handleChange}
                onChange={(e) => {
                    formik.setFieldValue('expireDate', e.target.checked)
                    if (!e.target.checked) {
                        formik.setFieldValue('endDateDay', '')
                        formik.setFieldValue('endDateMonth', '')
                        formik.setFieldValue('endDateYear', '')
                    }
                }}
                onBlur={formik.handleBlur}
                label={getTitle('7577', 'Expiry date')}
            />
            {formik.values.expireDate && (
                <ApplyV2DateSelectGroup
                    // label={getTitle('7577', 'Expiry date')}
                    isRequired
                    names={{
                        day: 'endDateDay',
                        month: 'endDateMonth',
                        year: 'endDateYear',
                    }}
                    value={{
                        day: formik.values.endDateDay,
                        month: formik.values.endDateMonth,
                        year: formik.values.endDateYear,
                    }}
                    onChange={({ day, month, year }) => {
                        formik.setFieldValue('endDateDay', day)
                        formik.setFieldValue('endDateMonth', month)
                        formik.setFieldValue('endDateYear', year)
                    }}
                    errors={{
                        day: formik.touched.endDateDay && formik.errors.endDateDay,
                        month: formik.touched.endDateMonth && formik.errors.endDateMonth,
                        year: formik.touched.endDateYear && formik.errors.endDateYear,
                    }}
                    onBlur={formik.handleBlur}
                    isDisabled={!formik.values.expireDate}
                />
            )}
            {/*<ApplyV2InputText*/}
            {/*    name="courseDescription"*/}
            {/*    label={getTitle('606', 'Description')}*/}
            {/*    value={formik.values.courseDescription}*/}
            {/*    onChange={formik.handleChange}*/}
            {/*    onBlur={formik.handleBlur}*/}
            {/*    errorMessage={formik.touched.courseDescription && formik.errors.courseDescription}*/}
            {/*    isRequired*/}
            {/*/>*/}
            <ApplyV2InputTextarea
                name="courseDescription"
                label={getTitle('606', 'Description')}
                value={formik.values.courseDescription}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errorMessage={formik.touched.courseDescription && formik.errors.courseDescription}
                isRequired
            />
            <ApplyV2AttachmentsUploader
                // className={`${baseClass}__files`}
                label={getTitle('1044', 'Attachments')}
                name="files"
                isRequired
                multipleCount={5}
                // isDisabled={true}
                files={formik.values.files}
                uploadingCount={fileUploadingCount}
                // accept={['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png']}
                accept={getAcceptFilesString('ATTACHMENTS')}
                onChange={handleFileChangeHandler}
                onBlur={formik.handleBlur}
                errorMessage={formik.touched.files && JSON.stringify(formik.errors.files)}
                loadingIds={deletingFilesIds}
                onDelete={handleFileDelete}
            />
            {/*<div className={`${baseClass}__files`}>*/}
            {/*    <FileUploaderV2*/}
            {/*        name="files"*/}
            {/*        multiple={true}*/}
            {/*        multipleLimit={3}*/}
            {/*        defaultMask={true}*/}
            {/*        accept={['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png']}*/}
            {/*        onChange={handleFileChange}*/}
            {/*        onBlur={formik.handleBlur}*/}
            {/*        onError={(errs) => {*/}
            {/*            console.log(errs)*/}
            {/*            // formik.setFieldValue(*/}
            {/*            //     'files',*/}
            {/*            //     errs.map((e) => e.error)*/}
            {/*            // )*/}
            {/*        }}*/}
            {/*    />*/}
            {/*    /!*<div>{formik.touched.files && 'TOUCHED'}</div>*!/*/}
            {/*    /!*<div>{Array.isArray(formik.errors.files) ? formik.errors.files.join(', ') : formik.errors.files}</div>*!/*/}
            {/*    <FileUploaderV2List*/}
            {/*        // files={item.files || []}*/}
            {/*        files={formik.values.files}*/}
            {/*        isShowParseInfo={false}*/}
            {/*        onDelete={handleFileDelete}*/}
            {/*        // loadingId={loadingFileId}*/}
            {/*        loadingIds={deletingFilesIds}*/}
            {/*        loadingCount={fileUploadingCount}*/}
            {/*    />*/}
            {/*</div>*/}
            <div className={`${baseClass}__btns`}>
                <ApplyV2Button
                    type={'reset'}
                    variant="transparent"
                    className={`${baseClass}__btns--btn ${baseClass}__btns--btn--submit`}
                    // isLoading={formik.isSubmitting}
                    onClick={() => {
                        formik.resetForm()
                        onClose()
                        // console.log(formik.values, formik.touched, formik.errors)
                    }}
                >
                    {getTitle('416', 'Cancel')}
                </ApplyV2Button>
                <ApplyV2Button
                    type={'submit'}
                    variant="primary"
                    className={`${baseClass}__btns--btn ${baseClass}__btns--btn--submit`}
                    isLoading={formik.isSubmitting}
                    // isDisabled={!formik.isValid || formik.isSubmitting || !formik.dirty}
                    // isDisabled={!formik.isValid || !formik.dirty}
                    // onClick={() => {
                    //     console.log('Submit clicked', formik.values)
                    //     console.log('Formik state:', formik.touched)
                    //     console.log('Formik errors:', formik.errors)
                    // }}
                >
                    {getTitle('12', 'Save')}
                </ApplyV2Button>
            </div>
        </form>
    )
}

export default ApplyV2CoursesStepEdit
